/**
 * Janela de 24h do WhatsApp + template HSM — lógica pura compartilhada.
 *
 * Nasceu no web (Item 27: `window-24h.ts` + `proactive-adapter.ts`). O app móvel
 * é o 2º consumidor e ficou sem ela — deixava digitar fora da janela e só
 * oferecia «Tentar novamente», que fora da janela nunca funciona (TestFlight
 * build 1, 07/Out/2026). Promovida para cá em vez de copiada.
 *
 * O Engine é o ENFORCER (`apps/engine/src/modules/integrations/
 * whatsapp-cloud-api/utils/window-24h.ts`: última inbound + 24h, `>=` fecha;
 * fora da janela sem `hsmFallback` ⇒ `failed` `outside-24h-window:<motivo>`).
 * Aqui é UX: aviso e seletor de template.
 */
export const META_FREE_WINDOW_MS = 24 * 60 * 60 * 1000;
/**
 * `messages` em ordem ASC (mais antiga primeiro). Edge honesto: se a última
 * inbound estiver fora da página carregada, deriva `no-inbound` — aceitável
 * porque o aviso não trava o composer.
 */
export function evaluateWindow24h(params) {
    if (params.channel !== 'whatsapp-cloud-api')
        return { state: 'not-applicable' };
    if (!params.messagesLoaded)
        return { state: 'unknown' };
    const now = params.now ?? new Date();
    let lastInboundAt = null;
    for (let i = params.messages.length - 1; i >= 0; i--) {
        const m = params.messages[i];
        if (m?.direction === 'inbound') {
            lastInboundAt = new Date(m.sentAt);
            break;
        }
    }
    if (lastInboundAt === null)
        return { state: 'closed', reason: 'no-inbound' };
    const elapsed = now.getTime() - lastInboundAt.getTime();
    if (elapsed >= META_FREE_WINDOW_MS)
        return { state: 'closed', reason: 'expired' };
    return { state: 'open', expiresAt: new Date(lastInboundAt.getTime() + META_FREE_WINDOW_MS) };
}
// ─── Template: variáveis índice-numérico + preview ────────────────────────
const VAR_RE = /\{\{(\d+)\}\}/g;
/** Índices `{{N}}` únicos, em ordem numérica. Sem bodyPreview → []. */
export function extractTemplateVars(bodyPreview) {
    if (bodyPreview === undefined || bodyPreview === '')
        return [];
    const keys = new Set();
    for (const m of bodyPreview.matchAll(VAR_RE)) {
        if (m[1] !== undefined)
            keys.add(m[1]);
    }
    return [...keys].sort((a, b) => Number(a) - Number(b));
}
/** Quebra o bodyPreview em partes para o preview destacar as variáveis. */
export function splitTemplate(bodyPreview, vars) {
    if (bodyPreview === undefined || bodyPreview === '')
        return [];
    const parts = [];
    let last = 0;
    for (const m of bodyPreview.matchAll(VAR_RE)) {
        const idx = m.index ?? 0;
        if (idx > last)
            parts.push({ type: 'text', value: bodyPreview.slice(last, idx) });
        const key = m[1] ?? '';
        // Espelha o render do backend (`value ?? match`): chave AUSENTE → placeholder;
        // valor vazio ('') → vazio (o preview é fiel ao que será enviado).
        parts.push({ type: 'var', value: vars[key] ?? `{{${key}}}`, key });
        last = idx + m[0].length;
    }
    if (last < bodyPreview.length)
        parts.push({ type: 'text', value: bodyPreview.slice(last) });
    return parts;
}
/**
 * Substituição final IDÊNTICA ao render do backend (`value ?? match`): chave
 * AUSENTE mantém o `{{N}}`; valor vazio ('') vira vazio.
 */
export function renderTemplateText(bodyPreview, vars) {
    if (bodyPreview === undefined)
        return '';
    return bodyPreview.replace(VAR_RE, (match, key) => vars[key] ?? match);
}
/**
 * Variáveis posicionais do `hsmFallback` (índice 0 = `{{1}}`), até o maior
 * índice usado. Buracos viram '' — o Engine valida os obrigatórios.
 */
export function buildTemplateVariables(varKeys, vars) {
    const lastKey = varKeys[varKeys.length - 1];
    const maxIdx = lastKey !== undefined ? Number(lastKey) : 0;
    return Array.from({ length: maxIdx }, (_, i) => vars[String(i + 1)] ?? '');
}
// ─── Falha de envio no caminho WhatsApp ───────────────────────────────────
/** O Engine marca `outside-24h-window` ou `outside-24h-window:<motivo>`. */
export function isOutsideWindowFailure(reason) {
    return typeof reason === 'string' && reason.startsWith('outside-24h-window');
}
/** Tradução dos `failureReason` do caminho WhatsApp; desconhecido → null. */
export function windowFailureHint(reason) {
    if (reason === null || reason === undefined)
        return null;
    if (isOutsideWindowFailure(reason)) {
        return 'Fora da janela de 24h do WhatsApp — mensagens livres não são entregues. Use «Enviar template» (template aprovado).';
    }
    if (reason.startsWith('no-approved-template')) {
        return 'O template indicado não está aprovado neste tenant.';
    }
    if (reason.startsWith('template-variable-mismatch')) {
        return 'O template exige mais variáveis do que as preenchidas.';
    }
    return null;
}
//# sourceMappingURL=window-24h.js.map